import { Input } from '@shared/components/ui/input';
import { useTranslation } from 'react-i18next';
import { getCurrencySymbol, useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { Rate } from '@modules/Rates/models/rate.types';

interface RatePricesTableProps {
  rates: Rate[];
  prices: Record<number, number>;
  onPriceChange: (rateId: number, value: string) => void;
  errors?: Record<number, string>;
}

export const RatePricesTable = ({ rates, prices, onPriceChange, errors }: RatePricesTableProps) => {
  const { t } = useTranslation('materialAlmacenModule');
  const currency = useCurrency();
  const currencySymbol = getCurrencySymbol(currency);

  if (rates.length === 0) {
    return (
      <p className="text-sm text-subtext py-4 text-center" data-testid="rate-prices-empty">
        {t('modal.create.rates_empty')}
      </p>
    );
  }

  return (
    <div className="border border-card-border rounded-lg overflow-hidden" data-testid="rate-prices-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-card-border/30 border-b border-card-border">
            <th className="text-left px-4 py-2 font-medium text-subtext">{t('modal.create.rates_columns.name')}</th>
            <th className="text-right px-4 py-2 font-medium text-subtext">
              {t('modal.create.rates_columns.price', { currency })}
            </th>
          </tr>
        </thead>
        <tbody>
          {rates.map((rate) => (
            <tr key={rate.id} className="border-b border-card-border last:border-b-0">
              <td className="px-4 py-3 text-title">{rate.name}</td>
              <td className="px-4 py-3 text-right">
                <div className="flex flex-col items-end gap-1">
                  <div className="relative inline-flex items-center">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={prices[rate.id] ?? ''}
                      onChange={(e) => onPriceChange(rate.id, e.target.value)}
                      className={`w-28 text-right pr-7 ${errors?.[rate.id] ? 'border-error' : ''}`}
                      data-testid={`rate-price-input-${rate.id}`}
                    />
                    <span className="absolute right-2 text-xs text-subtext pointer-events-none">{currencySymbol}</span>
                  </div>
                  {errors?.[rate.id] && <span className="text-xs text-error">{errors[rate.id]}</span>}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
